/*
  Wholesale quote form anchor. Any link on the page whose URL ends in the
  form's anchor (#quote-request by default) scrolls smoothly to the form,
  whichever way the link was entered in the theme editor:

    #quote-request
    /pages/wholesale#quote-request
    https://store.com/pages/wholesale#quote-request

  Links to the anchor on a different page are left alone so they navigate
  normally. Focus moves to the form section so keyboard and screen reader
  users land there too. Arriving with the anchor already in the address
  bar re-aligns once the page (and the Forms app) has loaded, since the
  browser's own jump happens before the content above has its final height.
*/
(function () {
  if (window.wsQuoteAnchorReady) return;
  window.wsQuoteAnchorReady = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function findTarget(hash) {
    if (!hash || hash.length < 2) return null;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (error) {
      return null;
    }
    const target = document.getElementById(id);
    return target && target.hasAttribute('data-ws-quote-anchor') ? target : null;
  }

  // Slow at both ends, quicker through the middle.
  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  let activeScroll = null;

  // Where the page must sit for the target to be at the top, less its
  // scroll-margin-top (room for the sticky header). Measured fresh each
  // time, so images or the Forms app loading mid-scroll can't throw it off.
  function destinationOf(target) {
    const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - margin;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    return Math.max(0, Math.min(top, max));
  }

  function jumpTo(y) {
    // 'instant' so a site-wide CSS scroll-behavior: smooth can't smooth each
    // frame a second time and make the animation stutter.
    window.scrollTo({ top: y, behavior: 'instant' });
  }

  function cancelScroll() {
    if (!activeScroll) return;
    cancelAnimationFrame(activeScroll.frame);
    ['wheel', 'touchstart', 'keydown'].forEach((type) =>
      window.removeEventListener(type, cancelScroll, { passive: true })
    );
    activeScroll = null;
  }

  function animateTo(target) {
    cancelScroll();

    const start = window.scrollY;
    const distance = Math.abs(destinationOf(target) - start);
    // Longer trips take longer, within a range that still feels responsive.
    const duration = Math.min(1400, Math.max(700, distance * 0.45));
    const startTime = performance.now();

    // The visitor taking over (wheel, touch, keys) stops the animation.
    ['wheel', 'touchstart', 'keydown'].forEach((type) =>
      window.addEventListener(type, cancelScroll, { passive: true, once: true })
    );

    activeScroll = { frame: 0 };
    const step = (now) => {
      if (!activeScroll) return;
      const progress = Math.min(1, (now - startTime) / duration);
      const end = destinationOf(target);
      jumpTo(start + (end - start) * easeInOutCubic(progress));
      if (progress < 1) {
        activeScroll.frame = requestAnimationFrame(step);
      } else {
        cancelScroll();
      }
    };
    activeScroll.frame = requestAnimationFrame(step);
  }

  function scrollToTarget(target, smooth) {
    if (smooth && !reduceMotion.matches) {
      animateTo(target);
    } else {
      jumpTo(destinationOf(target));
    }
    target.focus({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    if (!link || (link.target && link.target !== '_self')) return;

    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.href);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;

    const target = findTarget(url.hash);
    if (!target) return;

    event.preventDefault();
    if (window.location.hash !== url.hash) {
      history.pushState(null, '', url.hash);
    }
    scrollToTarget(target, true);
  });

  function alignOnArrival() {
    const target = findTarget(window.location.hash);
    if (target) scrollToTarget(target, false);
  }

  if (document.readyState === 'complete') {
    alignOnArrival();
  } else {
    window.addEventListener('load', alignOnArrival, { once: true });
  }
})();
